import { useCallback } from 'react';

import postMessageHelper from 'Util/Message/postMessageHelper';
import { TOPIC } from 'Util/Message/TopicName';
import { useClassroomContext } from 'Context/ClassroomContext';

import { useExceptionTracking } from './useExceptionTracking';
import { formatBangkokTime } from './utils';

const CATEGORY = 'Quiz Error';
const CATEGORY_SUCCESS = 'Quiz Success';

const QUIZ_ERRORS = {
  ANSWER_SUBMIT: 'Submit an answer',
  FINAL_ANSWERS_SUBMIT: 'Submit final answers',
  GRADE_PAPER: 'Grade paper',
  SUBMIT_QUIZ: 'Submit quiz',
  START_QUIZ: 'Start quiz',
};

const QUIZ_SUCCESS = {
  START: 'Start quiz',
  SUBMIT: 'Submit quiz',
};

const defaultCourseMode = 'TRADITIONAL';
const fallbackValue = '-';

export const genQuizError = (type, description) => ({
  description: `[${CATEGORY}: ${type}] ${new Date()} ${description}`,
  fatal: true,
});

export const genQuizSuccess = (type, description) => ({
  description: `[${CATEGORY_SUCCESS}: ${type}] ${new Date()} ${description}`,
  fatal: true,
});

// TODO: Should be named useQuizTracking
function useQuizExceptionTracking() {
  const { logError } = useExceptionTracking();
  const { courseData, currentSubsectionId, courseId } = useClassroomContext();
  const courseMode = courseData?.courseMode ?? defaultCourseMode;

  const logAnswerSubmitError = useCallback(
    (description) => {
      logError(genQuizError(QUIZ_ERRORS.ANSWER_SUBMIT, description));
    },
    [logError]
  );

  // NOTE: courseId is sourceId of cr_course or sourceCourseId of cr_classroom
  const logStartQuiz = useCallback(
    ({ quizId, subsectionTitle, subsectionContentType }) => {
      logError(genQuizSuccess(QUIZ_SUCCESS.START, `${courseId}|${courseData?.courseRevision}`));
      postMessageHelper(TOPIC.QUIZ_START, {
        courseId: courseId ?? fallbackValue,
        revision: courseData?.courseRevision ?? fallbackValue,
        quizId: quizId ?? fallbackValue,
        subsectionId: currentSubsectionId ?? fallbackValue,
        subsectionTitle: subsectionTitle ?? fallbackValue,
        subsectionContentType: subsectionContentType ?? fallbackValue,
        clientTimestamp: formatBangkokTime(),
        courseMode,
      });
    },
    [logError, courseId, courseData?.courseRevision, currentSubsectionId, courseMode]
  );

  const logSubmitQuiz = useCallback(
    ({ quizId, subsectionTitle, subsectionContentType }) => {
      logError(genQuizSuccess(QUIZ_SUCCESS.SUBMIT, `${courseId}|${courseData?.courseRevision}`));
      postMessageHelper(TOPIC.QUIZ_SUBMIT, {
        courseId: courseId ?? fallbackValue,
        revision: courseData?.courseRevision ?? fallbackValue,
        quizId: quizId ?? fallbackValue,
        subsectionId: currentSubsectionId ?? fallbackValue,
        subsectionTitle: subsectionTitle ?? fallbackValue,
        subsectionContentType: subsectionContentType ?? fallbackValue,
        clientTimestamp: formatBangkokTime(),
        courseMode,
      });
    },
    [courseData?.courseRevision, courseId, currentSubsectionId, logError, courseMode]
  );

  const logSubmitQuizError = useCallback(
    ({ quizId, errorMessage, subsectionTitle, subsectionContentType }) => {
      logError(genQuizError(QUIZ_ERRORS.SUBMIT_QUIZ, errorMessage));
      postMessageHelper(TOPIC.QUIZ_SUBMIT_ERROR, {
        courseId: courseId ?? fallbackValue,
        revision: courseData?.courseRevision ?? fallbackValue,
        quizId: quizId ?? fallbackValue,
        subsectionId: currentSubsectionId ?? fallbackValue,
        subsectionTitle: subsectionTitle ?? fallbackValue,
        subsectionContentType: subsectionContentType ?? fallbackValue,
        clientTimestamp: formatBangkokTime(),
        errorMessage: errorMessage ?? fallbackValue,
        courseMode,
      });
    },
    [courseData?.courseRevision, courseId, currentSubsectionId, logError, courseMode]
  );

  const logStartQuizError = useCallback(
    ({ quizId, errorMessage, subsectionTitle, subsectionContentType }) => {
      logError(genQuizError(QUIZ_ERRORS.START_QUIZ, errorMessage));
      postMessageHelper(TOPIC.QUIZ_START_ERROR, {
        courseId: courseId ?? fallbackValue,
        revision: courseData?.courseRevision ?? fallbackValue,
        quizId: quizId ?? fallbackValue,
        subsectionId: currentSubsectionId ?? fallbackValue,
        subsectionTitle: subsectionTitle ?? fallbackValue,
        subsectionContentType: subsectionContentType ?? fallbackValue,
        clientTimestamp: formatBangkokTime(),
        errorMessage: errorMessage ?? fallbackValue,
        courseMode,
      });
    },
    [courseData?.courseRevision, courseId, currentSubsectionId, logError, courseMode]
  );

  const logFinalAnswersSubmitError = useCallback(
    (description) => {
      logError(genQuizError(QUIZ_ERRORS.FINAL_ANSWERS_SUBMIT, description));
    },
    [logError]
  );

  const logGradePaperError = useCallback(
    (description) => {
      logError(genQuizError(QUIZ_ERRORS.GRADE_PAPER, description));
    },
    [logError]
  );

  return {
    logAnswerSubmitError,
    logFinalAnswersSubmitError,
    logGradePaperError,
    logStartQuiz,
    logSubmitQuiz,
    logSubmitQuizError,
    logStartQuizError,
  };
}

export { useQuizExceptionTracking };
